import { ServiceTypeEnum } from '@sr-sdks/api-sdk-axios';
import {
  Card,
  CardContent,
  Icon,
  Skeleton,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@sr/ui-library';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { queryOptionsGetTitle } from '../../../../api/titles/titlesApi';
import { SERVICE_BADGE_PRESETS } from '../../../../components/Badges/appBadgeConstants';
import { SingleQueryResults } from '../../../../components/QueryResults/SingleQueryResults';
import { useActiveWorkspace } from '../../../../hooks/useActiveWorkspace';
import { isRoyaltyServiceType } from '../../../../utils/helpers/common';
import { StatementsDataTable } from './StatementsDataTable';
import { VISIBLE_TABS } from './statementFilterTabs';

interface TitleStatementsSectionProps {
  titleId: string;
}

/**
 * MSR2's `SerialLevels` literal for a series root. Matched case-sensitively,
 * the same way the backend's Season rule in `royalties-season.ts` does.
 *
 * Deliberately narrower than `isSeriesTitleType`/`serialTitleId == null`: MSR2
 * maps `SerialTitleId` from `Claim.SerialId`, which can be null, so a parentless
 * Season would read as a series root under those and lose statements it shows
 * today. A missing `serialLevel` only costs the fix, never a working page.
 */
const SERIAL_HEADER_LEVEL = 'Serial';

/**
 * MSR3 Title Summary "Title Statements" card. Service-type tabs filter a
 * single paginated, sortable, searchable statements table scoped to the
 * title, or on a series' royalty tabs to that series' seasons.
 *
 * This card is the single home for a title's payment records across every
 * service type, including CAM/DAS — the Collection Account Management section
 * deliberately no longer carries a Payments tab, so the same invoice records
 * are never listed twice on one page. Per that decision the Disbursements tab
 * spans both `disbursements` and `collectionAccount` (see `statementFilterTabs.ts`),
 * otherwise CAM invoices would have no home on this page at all.
 *
 * Only tabs covering a service the title is actually registered under are shown —
 * a service the title doesn't have is hidden entirely rather than rendered as
 * an empty/not-enabled tab — and the view defaults to the first such tab so it
 * never opens on one that's empty. When a title has only a single enabled service,
 * the tab bar is hidden entirely since there is nothing to switch to, and the
 * table renders directly beneath the heading. The heading is static chrome that renders
 * immediately; the tab bar and table both sit behind a single `SingleQueryResults`
 * keyed on `titleQuery`, so a skeleton stands in for both while `title` is
 * loading (no premature "all tabs" flash) and a single error indicator replaces
 * both on failure. `enabledTabs` still defaults to every visible tab before
 * `title` loads, but only to keep `activeService` truthy for the top-level
 * render gate below — nothing user-visible reads it until the post-load success
 * render. Reuses `StatementsDataTable` for the table itself; this component
 * only owns the tab/filter state and the card chrome around it.
 */
export const TitleStatementsSection = ({
  titleId,
}: TitleStatementsSectionProps) => {
  const [activeWorkspace] = useActiveWorkspace();
  const titleQuery = useQuery(queryOptionsGetTitle(titleId));
  const title = titleQuery.data;

  // Royalty statements for a series hang off its seasons, so this card has to
  // query by serial header rather than by the title's own id (APP-2431).
  const serialTitleId =
    title?.serialLevel === SERIAL_HEADER_LEVEL ? titleId : undefined;

  const enabledTabs = useMemo(
    () =>
      title
        ? VISIBLE_TABS.filter(({ serviceTypes }) =>
            serviceTypes.some((service) => title.serviceType.includes(service)),
          )
        : VISIBLE_TABS,
    [title],
  );

  const [manualService, setManualService] = useState<ServiceTypeEnum | null>(
    null,
  );

  // Falls back to the first enabled tab whenever there's no manual selection,
  // or the manually-selected one isn't enabled on this title.
  const activeTab =
    enabledTabs.find(({ service }) => service === manualService) ??
    enabledTabs[0];
  const activeService = activeTab?.service;

  // Only the royalty tabs list a season per row. The Invoices-backed tabs stay
  // scoped to this title, so their Title column would repeat one name per row.
  const showsSeasonRows =
    !!serialTitleId && !!activeTab?.serviceTypes.every(isRoyaltyServiceType);

  // Show the tab bar only when there are multiple enabled services to switch between.
  const hasMultipleServices = enabledTabs.length > 1;

  // No statement services are enabled on this title at all — nothing for
  // this card to show. Loading/error states always yield a non-empty
  // `enabledTabs` (defaulting to every visible tab), so this only fires once `title`
  // has genuinely loaded with zero enabled services.
  return !activeTab ? null : (
    <div className="flex flex-col gap-5">
      <h2 className="text-base font-medium text-foreground">
        Title Statements
      </h2>

      <Card>
        <CardContent className="flex flex-col gap-4 p-6">
          <SingleQueryResults
            loadingFallback={
              <>
                <Skeleton className="h-9 w-64" />
                <Skeleton className="h-64 w-full" />
              </>
            }
            query={titleQuery}
          >
            {() => (
              <>
                {hasMultipleServices && (
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <Tabs
                      value={activeService}
                      onValueChange={(value) =>
                        setManualService(value as ServiceTypeEnum)
                      }
                    >
                      <TabsList>
                        {enabledTabs.map(({ service }) => (
                          <TabsTrigger
                            key={service}
                            className="gap-1.5"
                            value={service}
                          >
                            <Icon
                              {...SERVICE_BADGE_PRESETS[service].iconProps}
                              size="sm"
                            />
                            {SERVICE_BADGE_PRESETS[service].children}
                          </TabsTrigger>
                        ))}
                      </TabsList>
                    </Tabs>
                  </div>
                )}

                <StatementsDataTable
                  hideTitleColumn={!showsSeasonRows}
                  paginationKey="titleStatementsPage"
                  serialTitleId={serialTitleId}
                  serviceTypes={activeTab.serviceTypes}
                  showTopPagination
                  titleId={titleId}
                  workspaceId={activeWorkspace?._id}
                />
              </>
            )}
          </SingleQueryResults>
        </CardContent>
      </Card>
    </div>
  );
};
